import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, Trash2, Minus, Plus, ArrowRight, 
  ShoppingBag, MapPin, Phone, FileText, Check, PartyPopper 
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const Cart = () => {
  const { t, getLocalizedField } = useLanguage();
  const { items, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [isCheckout, setIsCheckout] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkoutData, setCheckoutData] = useState({
    address: '',
    phone: '',
    notes: '',
  });

  const total = getCartTotal();
  const shipping = total > 25 ? 0 : 4.99;
  const finalTotal = total + shipping;

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login?redirect=/cart');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, checkoutData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrderId(response.data.order_id);
      setOrderPlaced(true);
      clearCart();
    } catch (error) {
      console.error('Error placing order:', error);
    } finally {
      setLoading(false);
    }
  };

  // Order Success View
  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto px-4 text-center"
            data-testid="order-success"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-24 h-24 mx-auto mb-6 rounded-full gradient-primary flex items-center justify-center"
            >
              <PartyPopper className="w-12 h-12 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {t('checkout.orderPlaced')}
            </h1>
            <p className="text-slate-600 mb-6">
              {t('checkout.orderSuccess')}
            </p>
            <div className="card-glass p-6 mb-6">
              <p className="text-sm text-slate-500 mb-1">{t('checkout.orderNumber')}</p>
              <p className="text-lg font-mono font-semibold text-primary">{orderId}</p>
            </div>
            <p className="text-slate-500 mb-8">{t('checkout.thankYou')}</p>
            <Button onClick={() => navigate('/')} className="btn-primary">
              {t('checkout.backToHome')}
            </Button>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  // Empty Cart View
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto px-4 text-center"
            data-testid="empty-cart"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
              <ShoppingBag className="w-12 h-12 text-slate-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {t('cart.empty')}
            </h1>
            <p className="text-slate-500 mb-8">{t('cart.emptySubtitle')}</p>
            <Link to="/category/all">
              <Button className="btn-primary">
                {t('cart.continueShopping')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16" data-testid="cart-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 flex items-center gap-3"
          >
            <ShoppingCart className="w-8 h-8 text-primary" />
            {isCheckout ? t('checkout.title') : t('cart.title')}
          </motion.h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items / Checkout Form */}
            <div className="lg:col-span-2 space-y-4">
              <AnimatePresence mode="wait">
                {!isCheckout ? (
                  <motion.div
                    key="cart-items"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    {items.map((item, index) => (
                      <motion.div
                        key={item.product.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.1 }}
                        className="card-glass p-4 flex gap-4"
                        data-testid={`cart-item-${item.product.id}`}
                      >
                        {/* Product Image */}
                        <Link to={`/product/${item.product.id}`} className="flex-shrink-0">
                          <div className="w-24 h-24 rounded-xl overflow-hidden bg-slate-100">
                            <img
                              src={item.product.image}
                              alt={getLocalizedField(item.product, 'name')}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </Link>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <Link to={`/product/${item.product.id}`}>
                            <h3 className="font-semibold text-slate-900 hover:text-primary transition-colors">
                              {getLocalizedField(item.product, 'name')}
                            </h3>
                          </Link>
                          <p className="text-sm text-slate-500">{item.product.brand}</p>
                          <p className="text-sm text-slate-500">{item.product.unit}</p>
                          <p className="text-lg font-bold text-primary mt-1">
                            ${item.product.price.toFixed(2)}
                          </p>
                        </div>

                        {/* Quantity & Remove */}
                        <div className="flex flex-col items-end justify-between">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => removeFromCart(item.product.id)}
                            className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                            data-testid={`remove-item-${item.product.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </motion.button>

                          <div className="flex items-center gap-2">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                              className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                              data-testid={`decrease-qty-${item.product.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </motion.button>
                            <span className="w-8 text-center font-semibold">{item.quantity}</span>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                              className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                              data-testid={`increase-qty-${item.product.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <motion.form
                    key="checkout-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handlePlaceOrder}
                    className="card-glass p-6 space-y-6"
                    data-testid="checkout-form"
                  >
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        {t('checkout.address')}
                      </label>
                      <Input
                        value={checkoutData.address}
                        onChange={(e) => setCheckoutData(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="123 Main St, City, Country"
                        required
                        className="h-12"
                        data-testid="checkout-address"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                        <Phone className="w-4 h-4 text-primary" />
                        {t('checkout.phone')}
                      </label>
                      <Input
                        value={checkoutData.phone}
                        onChange={(e) => setCheckoutData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+1 (555) 123-4567"
                        required
                        className="h-12"
                        data-testid="checkout-phone"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                        <FileText className="w-4 h-4 text-primary" />
                        {t('checkout.notes')}
                      </label>
                      <Textarea
                        value={checkoutData.notes}
                        onChange={(e) => setCheckoutData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Any special instructions..."
                        rows={3}
                        data-testid="checkout-notes"
                      />
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-glass p-6 sticky top-28"
              >
                <h2 className="text-xl font-bold text-slate-900 mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-slate-600">
                    <span>{t('cart.subtotal')}</span>
                    <span className="font-semibold">${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>{t('cart.shipping')}</span>
                    <span className={shipping === 0 ? 'text-green-600 font-semibold' : 'font-semibold'}>
                      {shipping === 0 ? t('cart.free') : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  {shipping > 0 && (
                    <p className="text-xs text-slate-500">
                      Free shipping on orders over $25
                    </p>
                  )}
                  <div className="pt-4 border-t border-slate-200">
                    <div className="flex justify-between text-slate-900">
                      <span className="text-lg font-semibold">{t('cart.total')}</span>
                      <span className="text-2xl font-bold text-primary" data-testid="cart-total">
                        ${finalTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {!isCheckout ? (
                  <Button
                    onClick={() => {
                      if (!user) {
                        navigate('/login?redirect=/cart');
                      } else {
                        setIsCheckout(true);
                      }
                    }}
                    className="w-full btn-primary h-14 text-lg"
                    data-testid="checkout-btn"
                  >
                    {t('cart.checkout')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Button
                      onClick={handlePlaceOrder}
                      disabled={loading || !checkoutData.address || !checkoutData.phone}
                      className="w-full btn-primary h-14 text-lg"
                      data-testid="place-order-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                          />
                          Processing...
                        </span>
                      ) : (
                        <>
                          <Check className="mr-2 w-5 h-5" />
                          {t('checkout.placeOrder')}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCheckout(false)}
                      className="w-full"
                    >
                      {t('common.back')}
                    </Button>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Cart;
